// Hideo - the sounds settings (reactive, for the speaker button and Settings › Appearance) and who plays what.
// The engine is src/features/sounds.ts (Web Audio, OFF by default). installSounds (called by onReady, src/features/boot.ts,
// after the theme store's init) adds three document listeners in the capture phase that only listen, never stop or
// change an event, so their order with the other listeners does not matter:
//   pointerdown  the first gesture lets the audio start (browsers block it before) and the music, if it is on
//   click        "click" on buttons and links, "tab" on page tabs and the picker's tabs (not on the app dialogs' buttons,
//                which have "confirm" / "back", nor on the theme bubbles, which have "theme", nor on the speaker controls)
//   pointerover  "hover", with a mouse only, on the sidebar, the large tiles, page tabs and main buttons, at most every 90 ms
// and watches: the theme (its sounds and music), an online save running (the music is lowered), the teleprompter open
// (the music is silent). Dialogs ("open"), the thumbs-up ("ok"), the notice at the bottom ("notify"), messages
// ("error") and questions ("confirm" / "back") call playSound from src/stores/notices.ts and useDialogEntry.
import { defineStore } from 'pinia'
import { reactive, watch } from 'vue'
import { DEFAULT_SOUNDS, duckMusic, playSound, readSoundSettings, setSoundSettings, setSoundStyle, silenceMusic, storeSoundSettings,
  unlockAudio, type SoundSettings } from '../features/sounds'
import { useThemeStore } from './theme'
import { useSyncStore } from './sync'
import { useDialogsStore } from './dialogs'

export const useSoundsStore = defineStore('sounds', () => {
  const settings = reactive<SoundSettings>({ ...DEFAULT_SOUNDS })
  let loaded = false
  function load(): void {
    if (loaded) return
    loaded = true
    Object.assign(settings, readSoundSettings())
    setSoundSettings({ ...settings })
  }
  function set(patch: Partial<SoundSettings>): void {
    load()
    Object.assign(settings, patch)
    storeSoundSettings({ ...settings })
    setSoundSettings({ ...settings })
    unlockAudio()                       // always after a click or a slider: a user gesture
  }
  /** The speaker button: everything silent, or back on (unmuting plays a click, so the user hears the volume) */
  function toggleMute(): void {
    set({ muted: !settings.muted })
    if (!settings.muted) playSound('click')
  }
  return { settings, load, set, toggleMute }
})

const HOVER = '#mainNav button, .creator-type-btn, .ped-subtabs button, .style-tabs button, .style-bubble, button.bg-brand'
const CLICK = 'button, a[href], [role="button"], .pill-btn, input[type="checkbox"], input[type="radio"]'
const TABS = '.ped-subtabs button, .style-tabs button'
/** Their own sounds: app dialogs (notices store), theme bubbles (the theme change), the speaker controls */
const OWN = '.ui-dialog, .style-bubble, .sound-ctl'

let installed = false
/** The listeners and watches of the sounds (once) */
export function installSounds(doc: Document = document): void {
  if (installed) return
  installed = true
  const sounds = useSoundsStore(), theme = useThemeStore(), sync = useSyncStore(), dialogs = useDialogsStore()
  sounds.load()
  setSoundStyle(theme.curStyle())
  let lastHover = 0
  doc.addEventListener('pointerdown', () => unlockAudio(), true)
  doc.addEventListener('click', e => {
    const el = (e.target as Element | null)?.closest?.(CLICK) as HTMLButtonElement | null
    if (!el || el.disabled || el.closest(OWN)) return
    playSound(el.matches(TABS) ? 'tab' : 'click')
  }, true)
  doc.addEventListener('pointerover', e => {
    if ((e as PointerEvent).pointerType !== 'mouse') return
    const el = (e.target as Element | null)?.closest?.(HOVER) as HTMLButtonElement | null
    const from = (e as PointerEvent).relatedTarget as Node | null
    if (!el || el.disabled || (from && el.contains(from))) return
    const now = performance.now()
    if (now - lastHover < 90) return
    lastHover = now
    playSound('hover')
  }, true)
  // The theme changes: its sounds and music, and its own sound (after the classes are on <html>)
  const prevAfter = theme.hooks.afterSwitch
  theme.hooks.afterSwitch = (key, animate) => { prevAfter?.(key, animate); setSoundStyle(key); if (animate) playSound('theme') }
  watch(() => sync.busy || sync.AS.running, on => duckMusic(!!on))
  watch(() => dialogs.isOpen('teleprompter'), on => silenceMusic(on))
}
